import {HOTB} from "../HOTB";
import {gsap} from "gsap";
import {TimelineSprite} from "@nolimitcity/promo-panel/bin/utils/TimelineSprite";
import {DisplayObject} from "pixi.js";
import {GameUtils} from "./GameUtils";

export interface ISpineCreateConfig {
    path: string;
    pos: PIXI.Point;
    name?: string;
    initShow?: boolean;
}

export interface IShakeConfig {
    x: number;
    y: number;
    obj: DisplayObject;
    duration: number;
    Intensity?: number;
    hertz: number;
    ease?: any;
    reverseEase?: boolean;
    positionCb?: () => PIXI.Point;
}

export interface ShakeTarget {
    target: PIXI.Container;
    ogPos: PIXI.Point;
}

export class ISpinePlayConfig {
    spineObj: PIXI.spine.Spine;
    animation: string;
    playLoop?: boolean;
    skins?: string[];
    startTime?: number;
    endTime?: number;
}

export class AnimUtils {
    private static screenShakePlaying: boolean = false;

    private static _screenShakeTargets: ShakeTarget[] = [];

    static get screenShakeTargets(): ShakeTarget[] {
        return this._screenShakeTargets;
    }

    static set screenShakeTargets(value: ShakeTarget[]) {
        this._screenShakeTargets = value;
    }

    public static getStageContainer(): PIXI.Container {
        const bgLayer: PIXI.Container = GameUtils.getLayer(HOTB.gameBackground.name)!;
        return AnimUtils.fetchSuperContainer(bgLayer);
    }

    public static createNewSpine(config: ISpineCreateConfig): PIXI.spine.Spine {
        const spineObj: PIXI.spine.Spine = new PIXI.spine.Spine(HOTB.assetLoader.getSpineAsset(config.path)),
            spinePos: PIXI.Point = config.pos ? new PIXI.Point(config.pos.x, config.pos.y) : new PIXI.Point(360, 360);

        spineObj.name = config.name ? config.name : "";
        spineObj.position.set(spinePos.x, spinePos.y);
        spineObj.visible = config.initShow ? config.initShow : false;

        return spineObj;
    }

    public static resetSpine(spineObj: PIXI.spine.Spine, show: boolean = false): void {
        if (spineObj.state === null || spineObj.skeleton === null) {
            debugger;
            throw new Error("--gg-- check for null - resetSpine");
        } else {
            spineObj.state.clearTracks();
            spineObj.skeleton.setToSetupPose();
            spineObj.skeleton.setBonesToSetupPose();
            spineObj.skeleton.setSlotsToSetupPose();
            spineObj.state.setEmptyAnimation(0, 0);
            spineObj.visible = show;
        }
    }

    public static getSpineDuration(spineObj: PIXI.spine.Spine, animName: string): number {
        if (spineObj.state === null || spineObj.skeleton === null) {
            debugger;
            throw new Error("--gg-- check for null - getSpineDuration");
        } else {
            const actualDur: number = spineObj.skeleton.data.findAnimation(animName).duration;
            return Math.max(0.033, actualDur);
        }
    }

    public static playSpineAnimation(params: ISpinePlayConfig): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            currSpine: PIXI.spine.Spine = params.spineObj,
            animName: string = params.animation,
            skinsToApply: string[] = params.skins ? params.skins : ["default"],
            currSpineData: PIXI.spine.core.SkeletonData = currSpine.skeleton.data,
            isLooped: boolean = params.playLoop ? params.playLoop : false,
            animDur: number = this.getSpineDuration(currSpine, animName),
            startTime: number = params.startTime ? params.startTime : 0,
            endTime: number = params.endTime ? params.endTime : animDur;

        tl.to(this, {
            duration: Math.max(0.033, (endTime - startTime)),
            onStart: (): void => {
                if (currSpine.state === null || currSpine.skeleton === null) {
                    debugger;
                    throw new Error("--gg-- check for null - playSpineAnimation");
                } else {
                    this.resetSpine(currSpine, true);

                    const combinedSkin: PIXI.spine.core.Skin = new PIXI.spine.core.Skin("combinedSkin");
                    for (let i: number = 0; i < skinsToApply.length; i++) {
                        combinedSkin.addSkin(currSpineData.findSkin(skinsToApply[i]));
                    }

                    currSpine.skeleton.setSkin(combinedSkin);
                    currSpine.state.setAnimation(0, animName, isLooped);

                    if (params.startTime !== undefined) {
                        currSpine.state.tracks[0].trackTime = 0;
                        currSpine.update(startTime);
                    }

                    if (params.endTime !== undefined) {
                        currSpine.state.tracks[0].trackEnd = endTime;
                    }

                    currSpine.skeleton.setSlotsToSetupPose();
                }
            }
        });

        return tl;
    }

    public static pauseSpineAnimation(spineObj: PIXI.spine.Spine): void {
        if (spineObj.state.timeScale > 0) {
            spineObj.state.timeScale = 0;
        }
    }

    public static resumeSpineAnimation(spineObj: PIXI.spine.Spine): void {
        if (spineObj.state.timeScale === 0) {
            spineObj.state.timeScale = 1;
        }
    }

    public static createImageSeq(seqName: string): TimelineSprite {
        const imgSeq: TimelineSprite = new TimelineSprite(HOTB.assetLoader.getTextures(seqName));
        imgSeq.name = seqName;
        imgSeq.anchor.set(0.5);
        imgSeq.hide();
        return imgSeq;
    }

    public static startVibrate(power: number, objToShake: PIXI.Container = this.getStageContainer()): gsap.core.Timeline {
        return AnimUtils.vibrateIt({
            x: 0.2,
            y: 1,
            duration: 0.5 + (power * 0.2),
            hertz: 180 + (power * 60),
            Intensity: 8 + (power * 6.5),
            obj: objToShake,
            positionCb: () => new PIXI.Point(0, 0),
            ease: "power3.easeOut",
            reverseEase: true
        }).play();
    }

    public static fetchSuperContainer(t: PIXI.Container): PIXI.Container {
        return t.parent && t.name != "Stage" ? (this.fetchSuperContainer(t.parent)) : t;
    }

    public static playScreenShake(power: number = 1): gsap.core.Timeline {
        if (AnimUtils.screenShakePlaying) {
            return gsap.timeline();
        }

        const tl: gsap.core.Timeline = gsap.timeline({
            "onComplete": (): void => {
                AnimUtils.screenShakePlaying = false;
            }
        });

        tl.add((): void => {
            AnimUtils.screenShakePlaying = true;
        });

        const posOffset: number = 10 * power,
            shakeDur: number = 0.05;

        AnimUtils._screenShakeTargets.forEach(shakeTarget => {
            tl.add(gsap.to(shakeTarget.target.position, {
                x: shakeTarget.ogPos.x + posOffset,
                y: shakeTarget.ogPos.y + posOffset,
                duration: shakeDur
            }), 0);
            tl.add(gsap.to(shakeTarget.target.position, {
                x: shakeTarget.ogPos.x,
                y: shakeTarget.ogPos.y,
                duration: shakeDur
            }), ">");
        })
        return tl;
    }

    public static cleanCareFully(targetToClean: { children: any[] }): void {
        // Retrieve all active animations (both tweens and timelines) from the global timeline.
        const activeAnimations: (gsap.core.Timeline | gsap.core.Tween)[] = gsap.globalTimeline.getChildren(true, true, true);


        activeAnimations.forEach((animation: gsap.core.Timeline | gsap.core.Tween): void => {
            let affected: boolean = false;

            // For tweens
            if (typeof animation.targets === "function") {
                const targets = animation.targets();
                targets.forEach((target: any): void => {
                    if (targetToClean.children.includes(target)) {
                        affected = true;
                    }
                });
            }

            // For timelines
            if (this.isTimeline(animation)) {
                const childTweens: (gsap.core.Timeline | gsap.core.Tween)[] = animation.getChildren(true, true, false); // Get all tweens.
                childTweens.forEach((tween: gsap.core.Timeline | gsap.core.Tween): void => {
                    if (typeof tween.targets === "function") {
                        const targets = tween.targets();
                        targets.forEach((target: any): void => {
                            if (targetToClean.children.includes(target)) {
                                affected = true;
                            }
                        });
                    }
                });
            }

            // If any tween in this animation targets an element in the container, kill the animation.
            if (affected) {
                animation.kill();
            }
        });
    }

    // for playing spine animation which can be progressed to 1 along with tl on skip click
    public static createTimelineForAnimation(spine: PIXI.spine.Spine, trackIndex: number, animationName: string, loop: boolean, animationAlpha: number = 1, mixDuration: number = 0, resetOnComplete: boolean = true): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
            repeat: loop ? -1 : 0
        });

        if (!spine.spineData || !spine.state) {
            return tl;
        }

        const animation: PIXI.spine.core.Animation = spine.spineData.findAnimation(animationName);
        if (!animation) {
            console.error(`No animation named ${animationName}`);

            return tl;
        }

        spine.autoUpdate = false;

        const setAnimation: () => void = () => {
            if (spine.state) {
                const trackEntry: PIXI.spine.core.TrackEntry = spine.state.setAnimationWith(trackIndex, animation, false);
                trackEntry.alpha = animationAlpha;
                trackEntry.mixDuration = mixDuration;
            }
        };

        const target = {
            playHead: 0,
            spine: spine,
            prevTime: 0
        };

        const updateFunc: () => void = () => {
            if (spine && target && spine.state) {
                const now = target.playHead;
                spine.update(now - target.prevTime);
                target.prevTime = now;
            }
        };

        tl.add(() => {
            target.prevTime = 0;
            setAnimation();
        });
        tl.fromTo(target, {playHead: 0}, {
            duration: animation.duration,
            playHead: animation.duration,
            onUpdate: updateFunc,
            ease: "none"
        });
        if (resetOnComplete) {
            tl.add(() => {
                target.prevTime = 0;
                setAnimation();
            });
        }
        tl.to(target, {duration: 0.0001}); //Needed so gsap does not skip "setAnimation" during reverse play.
        tl.add(() => {
            target.playHead = animation.duration;
            updateFunc();
        });

        return tl;
    }

    // Type guard to check if an animation is a Timeline.
    private static isTimeline(animation: gsap.core.Tween | gsap.core.Timeline): animation is gsap.core.Timeline {
        return "getChildren" in animation && typeof (animation as any).getChildren === "function";
    }

    private static vibrateIt(config: IShakeConfig): gsap.core.Timeline {
        let startPosX: number = config.obj.x,
            startPosY: number = config.obj.y,
            t: boolean = true;

        const tl: gsap.core.Timeline = gsap.timeline({
            onStart: (): void => {
                startPosX = config.obj.x;
                startPosY = config.obj.y;
            },
            onComplete: (): void => {
                config.obj.x = startPosX;
                config.obj.y = startPosY;

                if (config.positionCb) {
                    config.obj.x = config.positionCb().x;
                    config.obj.y = config.positionCb().y;
                }
            },
            onUpdate: (): void => {
                const nt: number = tl.progress(),
                    lt: number = tl.time();

                let et: number = nt;

                if (t) {
                    t = false;
                    return;
                }

                if (config.positionCb) {
                    startPosX = config.positionCb().x;
                    startPosY = config.positionCb().y;
                }

                if (config.reverseEase)
                    et = 1 - et;
                if (config.Intensity) {
                    if (config.y != 0) {
                        config.obj.y = startPosY + Math.cos(lt * config.hertz) * (config.Intensity * et) * config.y;
                    }

                    if (config.x != 0) {
                        config.obj.x = startPosX + (Math.sin(lt * config.hertz) * (config.Intensity * et)) * config.x;
                    }
                } else {
                    if (config.y != 0) {
                        config.obj.y = startPosY + (Math.cos(lt * config.hertz) * et) * config.y;
                    }

                    if (config.x != 0) {
                        config.obj.x = startPosX + (Math.sin(lt * config.hertz) * et) * config.x;
                    }
                }
            }
        });
        tl.to(this, {
            duration: config.duration
        });
        return tl;
    }
}